import { Component, computed, OnDestroy, Signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { ValidationErrorsComponent } from '../../../../../common/validation-errors/validation-errors.component';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { AccordionGroupComponent } from '../../../../../common/accordion-group/accordion-group.component';
import { AccordionItemComponent } from '../../../../../common/accordion-item/accordion-item.component';
import { SubpageContentByUrlPipe } from '../../../../../pipes/subpage-content-by-url.pipe';
import { AgreementForm } from './forms/agreement.form';
import { Store } from '@ngrx/store';
import { ApplicationAppEntityApplicationRead, SubpageAppEntitySubpageRead } from '../../../../../openapi';
import { selectApplication, selectLoading, selectPending } from '../../../../../store/selectors/application.selectors';
import { selectSubpages } from '../../../../../store/selectors/app.selectors';
import { updateApplication } from '../../../../../store/actions/application.actions';
import { Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { StagesComponent } from '../../../../../common/stages/stages.component';

export interface AgreementsDataComponentConfig {
  stages: string[];
  successUrl: string;
  cancelUrl: string;
}

@Component({
  selector: 'app-agreements-data-section',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ValidationErrorsComponent,
    SpinnerComponent,
    AccordionGroupComponent,
    AccordionItemComponent,
    SubpageContentByUrlPipe,
    StagesComponent
  ],
  providers: [
    AgreementForm
  ],
  templateUrl: './agreements-data-section.component.html'
})
export class AgreementsDataSectionComponent implements OnDestroy {

  public successUrl: string | undefined = this.route.snapshot.data['successUrl'];

  public cancelUrl: string | undefined = this.route.snapshot.data['cancelUrl'];

  public stages: string[] = this.route.snapshot.data['stages'];

  public applicationId: string | undefined = this.route.snapshot.params['appId'];

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  public rodoContent: Signal<string | null> = computed(() => this.application()?.service?.gdpr || null);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  public subpages: Signal<SubpageAppEntitySubpageRead[]> = this.store$.selectSignal(selectSubpages);

  private fetchApplication: Subscription = toObservable(this.application).subscribe((app) => {
    if (app) {
      if (app?.partialTypeExemption === 'Karta dużej rodziny') {
        this.form.createPartialTypeExemptionDeclareControl();
      }
      this.form.patchValue(app);
    }
  });

  constructor(
    public form: AgreementForm,
    private router: Router,
    private route: ActivatedRoute,
    private store$: Store
  ) {
    if ([
      'wniosek-do-projektu-aktu-planowania-przestrzennego',
      'uwaga-do-konsultowanego-projaktu-aktu-planowania-przestrzennego',
      'wniosek-o-zmiane-aktu-planowania-przestrzennego',
      'wniosek-o-sporządzenie-aktu-planowania-przestrzennego'
    ].includes(this.route.parent?.snapshot.data['contentUrl'])) {
      this.form.createElectronicCorrespondenceConsentControl();
    }
  }

  public submitAndGoNext(): void {
    if (this.form.valid) {
      this.store$.dispatch(updateApplication({
        successFnc: () => {
          this.router.navigate([this.successUrl], { relativeTo: this.route });
        },
        failureFnc: () => {
        },
        application: this.form.toWriteModel(),
        id: this.applicationId || ''
      }));
    } else {
      this.form.markAllAsTouched();
    }
  }

  public onCancel(): void {
    this.router.navigate([this.cancelUrl], { relativeTo: this.route });
  }

  public ngOnDestroy(): void {
    this.fetchApplication.unsubscribe();
  }
}
